import { useState, useEffect } from "react";
import PageMeta from "../../components/common/PageMeta";
import DateRangePicker from "../../components/daterange/DateRangePicker";
import api from "../../services/api";
import SalesAbandonedMetrics from "../../components/sales-abandoned/SalesAbandonedMetrics";
import SalesAbandonedTable from "../../components/sales-abandoned/SalesAbandonedTable";

// Interface para o tipo de dados da API
interface AbandonedData {
    summary: {
        valorTotal: number;
        totalAbandonos: number;
    };
    carts: Array<any>;
}

export default function MySalesAbandoned() {
    // Estados para pesquisa e filtro
    const [searchTermProdutos, setSearchTermProdutos] = useState("");

    // Estados para os dados da API
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [abandonedData, setAbandonedData] = useState<AbandonedData | null>(null);

    // Carregar datas do localStorage (se existirem) ou definir padrão
    const getSavedDateRange = () => {
        try {
            const savedRange = localStorage.getItem('dateRangeSelection');
            if (savedRange) {
                const { startDate, endDate } = JSON.parse(savedRange);
                return {
                    startDate: startDate ? new Date(startDate) : null,
                    endDate: endDate ? new Date(endDate) : null
                };
            }
        } catch (error) {
            console.error("Erro ao recuperar datas do localStorage:", error);
        }

        // Se não houver dados salvos ou houver erro, definir últimos 7 dias
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(today.getDate() - 6);

        return {
            startDate: sevenDaysAgo,
            endDate: today
        };
    };

    // Estado para armazenar as datas selecionadas
    const [dateRange, setDateRange] = useState<{ startDate: Date | null; endDate: Date | null }>(getSavedDateRange());

    // Função para buscar os dados de vendas
    const fetchAbandonedData = async (startDate?: Date | null, endDate?: Date | null) => {
        try {
            setIsLoading(true);

            // Preparar parâmetros de data se fornecidos
            let url = '/api/cart-abandoned';
            const params = new URLSearchParams();

            if (startDate && endDate) {
                // Função auxiliar para formatar a data no fuso horário brasileiro
                const formatDateBR = (date: Date, endOfDay = false) => {
                    // Cria uma cópia da data para não modificar a original
                    const dateCopy = new Date(date);

                    // Se for fim do dia, ajusta para 23:59:59
                    if (endOfDay) {
                        dateCopy.setHours(23, 59, 59, 999);
                    } else {
                        dateCopy.setHours(0, 0, 0, 0);
                    }

                    // Ajustar o fuso horário para GMT-3 (Brasil)
                    // Usar o timezone de Brasília (GMT-3)
                    return dateCopy.toLocaleString('pt-BR', {
                        timeZone: 'America/Sao_Paulo',
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                    }).replace(/(\d+)\/(\d+)\/(\d+).*?(\d+):(\d+):(\d+)/, '$3-$2-$1T$4:$5:$6-03:00');
                };

                // Data inicial começa às 00:00:00 no fuso horário brasileiro
                params.append('startDate', formatDateBR(startDate));

                // Data final vai até 23:59:59 no fuso horário brasileiro
                params.append('endDate', formatDateBR(endDate, true));

                url = `${url}?${params.toString()}`;
            }

            // Chamada para a API
            const response = await api.get(url);

            if (response.data && response.data.status === 'success') {
                setAbandonedData(response.data.data);
            } else {
                console.error("Erro ao carregar dados de abandono:", response.data);
                // Exibir mensagem de erro
                alert("Erro ao carregar os dados de abandono");
            }
        } catch (error) {
            console.error("Erro na requisição:", error);
            // Exibir mensagem de erro
            alert("Erro, por favor faça login novamente");
        } finally {
            setIsLoading(false);
        }
    };

    // Efeito para carregar os dados quando o componente é montado
    useEffect(() => {
        // Garantir que temos datas válidas para buscar
        if (dateRange.startDate && dateRange.endDate) {
            fetchAbandonedData(dateRange.startDate, dateRange.endDate);
        }
    }, [dateRange]); // Recarregar quando o intervalo de datas mudar

    // Função para quando o período de datas mudar
    const handleDateRangeChange = (range: { startDate: Date | null; endDate: Date | null }) => {
        // Armazenar o novo intervalo de datas selecionado
        setDateRange({
            startDate: range.startDate,
            endDate: range.endDate
        });

        // Não precisamos chamar fetchSalesData aqui, pois o useEffect acima será acionado
        // quando dateRange mudar e fará a chamada para nós
        console.log("Intervalo de datas atualizado:", range);
    };

    return (
        <>
            <PageMeta
                title="Sellyo - Vendas Abandonadas"
                description="Minhas Vendas Abandonadas"
            />
            <div className="grid grid-cols-12 gap-4 md:gap-6">
                <div className="col-span-12">
                    <div className="flex justify-between items-center">
                        <div className="w-auto">
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                                Vendas Abandonadas
                            </h3>
                        </div>
                        <div className="w-auto">
                            <DateRangePicker
                                onChange={handleDateRangeChange}
                                initialStartDate={dateRange.startDate}
                                initialEndDate={dateRange.endDate}
                            />
                        </div>
                    </div>
                </div>

                <div className="col-span-12 space-y-6">
                    <SalesAbandonedMetrics
                        summary={abandonedData?.summary}
                        isLoading={isLoading}
                    />
                    {/* Filtros acima da tabela */}
                    <div className="mb-6">
                        <div className="flex flex-col md:flex-row gap-4">
                            {/* Campo de busca */}
                            <div className="relative flex-1">
                                <button className="absolute -translate-y-1/2 left-4 top-1/2">
                                    <svg
                                        className="fill-gray-500 dark:fill-gray-400"
                                        width="20"
                                        height="20"
                                        viewBox="0 0 20 20"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M3.04175 9.37363C3.04175 5.87693 5.87711 3.04199 9.37508 3.04199C12.8731 3.04199 15.7084 5.87693 15.7084 9.37363C15.7084 12.8703 12.8731 15.7053 9.37508 15.7053C5.87711 15.7053 3.04175 12.8703 3.04175 9.37363ZM9.37508 1.54199C5.04902 1.54199 1.54175 5.04817 1.54175 9.37363C1.54175 13.6991 5.04902 17.2053 9.37508 17.2053C11.2674 17.2053 13.003 16.5344 14.357 15.4176L17.177 18.238C17.4699 18.5309 17.9448 18.5309 18.2377 18.238C18.5306 17.9451 18.5306 17.4703 18.2377 17.1774L15.418 14.3573C16.5365 13.0033 17.2084 11.2669 17.2084 9.37363C17.2084 5.04817 13.7011 1.54199 9.37508 1.54199Z"
                                            fill=""
                                        />
                                    </svg>
                                </button>
                                <input
                                    type="text"
                                    placeholder="Pesquisar abandono..."
                                    value={searchTermProdutos}
                                    onChange={(e) => setSearchTermProdutos(e.target.value)}
                                    className="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-200 bg-transparent py-2.5 pl-12 pr-4 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-800 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
                                />
                            </div>
                        </div>
                    </div>
                    <SalesAbandonedTable
                        searchTerm={searchTermProdutos}
                        carts={abandonedData?.carts}
                        isLoading={isLoading}
                    />
                </div>
            </div>
        </>
    );
}